/* Marastoni: Shopify price/inventory/quantity are always whole boxes. */
(() => {
  function calculate(area, coverage, waste, min = 1, step = 1) {
    const required = area * (1 + Number(waste) / 100);
    // Ignore binary floating point noise at exact box boundaries.
    const raw = Math.ceil(required / coverage - 1e-10);
    const boxes = Math.ceil(Math.max(raw, min) / step) * step;
    return { boxes, area: boxes * coverage };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculate };
  if (typeof window === 'undefined' || customElements.get('tile-calculator')) return;
  class TileCalculator extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      if (!this.form) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      const infoToggle = this.querySelector('[data-tile-waste-info-toggle]');
      const infoPanel = this.querySelector('[data-tile-waste-info]');
      if (infoToggle && infoPanel) {
        const closeInfo = (restoreFocus = false) => {
          infoPanel.hidden = true;
          infoToggle.setAttribute('aria-expanded', 'false');
          if (restoreFocus) infoToggle.focus();
        };
        infoToggle.addEventListener('click', () => {
          const expanded = infoToggle.getAttribute('aria-expanded') !== 'true';
          infoToggle.setAttribute('aria-expanded', String(expanded));
          infoPanel.hidden = !expanded;
        }, options);
        infoPanel.querySelector('[data-tile-info-close]').addEventListener('click', () => closeInfo(true), options);
        document.addEventListener('click', event => {
          if (!infoPanel.hidden && !infoPanel.contains(event.target) && !infoToggle.contains(event.target)) closeInfo();
        }, options);
        document.addEventListener('keydown', event => {
          if (event.key === 'Escape' && !infoPanel.hidden) closeInfo(infoPanel.contains(document.activeElement) || document.activeElement === infoToggle);
        }, options);
        document.addEventListener('focusin', event => {
          if (!infoPanel.hidden && !infoPanel.contains(event.target) && !infoToggle.contains(event.target)) closeInfo();
        }, options);
      }
      this.variants = JSON.parse(this.querySelector('[data-tile-variants]').textContent);
      this.i18n = JSON.parse(this.querySelector('[data-tile-i18n]').textContent);
      this.variantId = this.dataset.variantId;
      this.coverage = Number(this.dataset.coverage);
      this.areaInput = this.querySelector('[data-tile-area]');
      this.waste = this.querySelector('[data-tile-waste]');
      this.boxInput = this.querySelector('[data-tile-box-input]');
      this.mode = 'area';
      this.quantity = this.querySelector('[data-tile-quantity]');
      this.productRoot = this.closest('.product-info');
      this.displayCurrency = String(this.productRoot?.dataset.marastoniDisplayCurrency || this.dataset.currency || 'DKK').toUpperCase();
      const initialDisplayUnitPrice = Number(this.productRoot?.dataset.marastoniDisplayUnitPrice);
      this.displayUnitPrice = Number.isFinite(initialDisplayUnitPrice) && initialDisplayUnitPrice > 0 ? initialDisplayUnitPrice : null;
      this.money = this.createMoneyFormatter(this.displayCurrency);
      this.number = new Intl.NumberFormat(this.i18n.locale || 'en-GB', { maximumFractionDigits: 5 });
      this.inputNumber = new Intl.NumberFormat(this.i18n.locale || 'en-GB', { maximumFractionDigits: 5, useGrouping: false });
      this.areaInput.addEventListener('input', () => { this.mode = 'area'; this.update(); }, options);
      this.boxInput.addEventListener('input', () => {
        this.mode = 'boxes';
        this.setWaste(0);
        this.update();
      }, options);
      this.setWaste(this.dataset.defaultWaste ?? 10);
      this.querySelectorAll('[data-tile-waste-preset]').forEach(button => button.addEventListener('click', () => {
        this.mode = 'area'; this.setWaste(button.dataset.tileWastePreset); this.update();
      }, options));
      this.form.addEventListener('variant:change', (event) => {
        this.variantId = String(event.detail.variant?.id || '');
        // The price block is re-rendered by Impact and will emit the current
        // display currency + rounded m² price again. Until then, keep the
        // selected currency but avoid applying the previous variant's m² price.
        this.displayUnitPrice = null;
        this.update();
      }, options);
      this.productRoot?.addEventListener('marastoni:currency-change', (event) => {
        const currency = String(event.detail?.currency || '').toUpperCase();
        const unitPrice = Number(event.detail?.unitPrice);
        if (!currency || !Number.isFinite(unitPrice) || unitPrice <= 0) return;
        this.displayCurrency = currency;
        this.displayUnitPrice = unitPrice;
        this.money = this.createMoneyFormatter(currency);
        this.update();
      }, options);
      // Capture runs before Impact builds FormData, including keyboard submission.
      this.form.addEventListener('submit', (event) => {
        if (!this.update()) {
          event.preventDefault(); event.stopImmediatePropagation(); this.areaInput.reportValidity();
        }
      }, { ...options, capture: true });
      // The payment provider may use a closed shadow root; gate its outer container.
      this.form.addEventListener('click', (event) => {
        const checkout = event.target.closest('[data-tile-checkout]');
        if (checkout && !this.update()) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.areaInput.reportValidity();
        }
      }, { ...options, capture: true });
      this.update();
    }

    updateScrollAffordance() {
      const grid = this.querySelector('.tile-size-grid');
      const shell = this.querySelector('[data-tile-scroll-shell]');
      if (!grid || !shell) return;
      const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth);
      const current = Math.max(0, Math.min(maxScroll, grid.scrollLeft));
      const threshold = 3;
      shell.classList.toggle('has-overflow-left', maxScroll > threshold && current > threshold);
      shell.classList.toggle('has-overflow-right', maxScroll > threshold && current < maxScroll - threshold);
    }
    updateProgress(selectedCard = null) {
      const progress = this.querySelector('.tile-size-progress');
      const cards = Array.from(this.querySelectorAll('[data-tile-variant]'));
      if (!progress || cards.length === 0) return;

      const selected = selectedCard || cards.find(card => card.classList.contains('is-selected') || card.getAttribute('aria-current') === 'true') || cards[0];
      const selectedIndex = Math.max(0, cards.indexOf(selected));
      const progressRect = progress.getBoundingClientRect();
      if (progressRect.width <= 0) return;

      const centers = cards.map(card => {
        const rect = card.getBoundingClientRect();
        return rect.left + rect.width / 2;
      });

      // The active segment follows the real visual card positions, not equal fractions
      // of the full row. This keeps it centered below the chosen swatch. For the
      // first/last swatch the segment meets the exact start/end of the grey line.
      const startViewport = selectedIndex === 0
        ? progressRect.left
        : (centers[selectedIndex - 1] + centers[selectedIndex]) / 2;
      const endViewport = selectedIndex === cards.length - 1
        ? progressRect.right
        : (centers[selectedIndex] + centers[selectedIndex + 1]) / 2;

      const left = Math.max(0, Math.min(progressRect.width, startViewport - progressRect.left));
      const right = Math.max(left, Math.min(progressRect.width, endViewport - progressRect.left));
      const nextLeft = `${left.toFixed(2)}px`;
      const nextWidth = `${Math.max(1, right - left).toFixed(2)}px`;
      if (progress.style.getPropertyValue('--tile-progress-left') !== nextLeft) {
        progress.style.setProperty('--tile-progress-left', nextLeft);
      }
      if (progress.style.getPropertyValue('--tile-progress-width') !== nextWidth) {
        progress.style.setProperty('--tile-progress-width', nextWidth);
      }
    }
    disconnectedCallback() {
      if (this.progressFrame) cancelAnimationFrame(this.progressFrame);
      this.progressResizeObserver?.disconnect();
      this.abort?.abort();
    }
    text(key, values = {}) {
      let output = this.i18n?.[key] || '';
      for (const [name, value] of Object.entries(values)) output = output.replaceAll(`{${name}}`, String(value));
      return output;
    }
    createMoneyFormatter(currency) {
      const locale = this.i18n?.locale || 'en-GB';
      const active = String(currency || this.dataset.currency || 'DKK').toUpperCase();
      return {
        format(value) {
          const amount = Number(value);
          if (!Number.isFinite(amount)) return '—';
          const isWhole = Math.abs(amount - Math.round(amount)) < 1e-9;
          const number = new Intl.NumberFormat(locale, {
            useGrouping: true,
            minimumFractionDigits: 0,
            maximumFractionDigits: isWhole ? 0 : 2
          }).format(amount);
          if (active === 'EUR') return `€${number}`;
          if (active === 'USD') return `USD ${number}`;
          if (active === 'DKK') return `DKK ${number}`;
          return `${active} ${number}`;
        }
      };
    }
    setWaste(value) {
      const requested = Number(value);
      const selected = [0, 10, 15, 20].includes(requested) ? requested : 10;
      this.waste.value = String(selected);
      this.querySelectorAll('[data-tile-waste-preset]').forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.tileWastePreset) === selected));
      });
      const note = this.querySelector('[data-tile-waste-note]');
      if (note) {
        note.textContent = selected === 0
          ? this.text('noteZero')
          : this.text('noteWaste', { waste: selected });
      }
    }
    update() {
      const text = this.areaInput.value.trim();
      let area = /^[0-9]+([,.][0-9]+)?$/.test(text) ? Number(text.replace(',', '.')) : NaN;
      const variant = this.variants[this.variantId];
      this.coverage = Number(variant?.coverage || 0);
      const coverageValid = Number.isFinite(this.coverage) && this.coverage > 0;
      this.querySelector('[data-tile-pack-info]').textContent = coverageValid
        ? this.text('packInfo', { coverage: this.number.format(this.coverage) })
        : this.text('packMissing');
      const pieces = this.querySelector('[data-tile-pieces]');
      pieces.hidden = !(variant?.pieces > 0);
      pieces.textContent = variant?.pieces > 0 ? this.text('pieces', { pieces: this.number.format(variant.pieces) }) : '';
      this.querySelector('[data-tile-pack-property]').value = coverageValid ? String(this.coverage) : '';
      let error = '';
      if (this.mode === 'boxes') {
        const boxes = /^[0-9]+$/.test(this.boxInput.value.trim()) ? Number(this.boxInput.value) : NaN;
        area = boxes * this.coverage;
        this.areaInput.value = Number.isFinite(area) && area > 0 ? this.inputNumber.format(Number(area.toFixed(8))) : '';
      }
      if (!Number.isFinite(area) || area <= 0) error = this.text('areaError');
      if (!coverageValid) error = this.text('coverageError');
      if (!variant?.available) error = this.text('unavailable');
      const wasteText = this.waste.value.trim();
      const waste = /^[0-9]+([,.][0-9]+)?$/.test(wasteText) ? Number(wasteText.replace(',', '.')) : NaN;
      if (![0, 10, 15, 20].includes(waste)) error = this.text('wasteError');
      let result = !error && calculate(area, this.coverage, waste, variant.min, variant.step);
      if (result && (!Number.isSafeInteger(result.boxes) || result.boxes > 999999)) error = this.text('largeOrder');
      if (result && variant.max && result.boxes > variant.max) error = this.text('maxBoxes', { max: this.number.format(variant.max) });
      this.areaInput.setCustomValidity(error);
      this.querySelector('[data-tile-error]').textContent = text || !variant?.available || !coverageValid ? error : '';
      const nextQuantity = error ? '1' : String(result.boxes);
      const quantityChanged = this.quantity.value !== nextQuantity;
      this.quantity.value = nextQuantity;
      this.quantity.setAttribute('value', nextQuantity);
      if (quantityChanged) {
        this.quantity.dispatchEvent(new Event('input', { bubbles: true }));
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.mode === 'area') this.boxInput.value = error ? '' : String(result.boxes);
      this.boxInput.setCustomValidity(this.mode === 'boxes' ? error : '');
      this.querySelector('[data-tile-boxes]').textContent = error ? '—' : this.number.format(result.boxes);
      this.querySelector('[data-tile-coverage]').textContent = error ? '—' : `${this.number.format(result.area)} m²`;
      let price = variant?.price || 0;
      if (!error) for (const tier of [...(variant.breaks || [])].sort((a,b) => a.min-b.min)) if (result.boxes >= tier.min) price = tier.price;
      const sourceTotal = result ? result.boxes * price / 100 : 0;
      // When the product price selector has supplied a display m² price, use
      // exactly that price for the calculator total. This keeps DKK/EUR/USD
      // totals visually consistent with the selected/default market price.
      const displayTotal = !error && Number.isFinite(this.displayUnitPrice) && this.displayUnitPrice > 0
        ? result.area * this.displayUnitPrice
        : sourceTotal;
      this.querySelector('[data-tile-total]').textContent = error ? '—' : this.money.format(displayTotal);
      this.form.querySelectorAll('[type="submit"]').forEach(button => { button.disabled = Boolean(error) || this.form.dataset.tileLoading === 'true'; });
      const ready = !error && this.form.dataset.tileLoading !== 'true';
      this.form.querySelectorAll('[data-tile-checkout]').forEach(checkout => {
        checkout.setAttribute('aria-disabled', String(!ready));
        checkout.toggleAttribute('inert', !ready);
      });
      return ready;
    }
  }
  customElements.define('tile-calculator', TileCalculator);
})();

/* Delegate variant fetching, media, price and URL updates to Impact's own picker. */
if (typeof window !== 'undefined' && !customElements.get('tile-variant-table')) {
  customElements.define('tile-variant-table', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const grid = this.querySelector('.tile-size-grid');
      this.updateProgress();
      if (grid) {
        const scheduleProgressUpdate = () => {
          if (this.progressFrame) return;
          this.progressFrame = requestAnimationFrame(() => {
            this.progressFrame = 0;
            this.updateProgress();
            this.updateScrollAffordance();
          });
        };
        grid.addEventListener('scroll', scheduleProgressUpdate, {signal: this.abort.signal, passive: true});
        this.progressResizeObserver = new ResizeObserver(scheduleProgressUpdate);
        this.progressResizeObserver.observe(grid);

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const scrollByVariant = direction => {
          const card = grid.querySelector('[data-tile-variant]');
          if (!card) return;
          const styles = getComputedStyle(grid);
          const gap = parseFloat(styles.columnGap || styles.gap) || 0;
          const amount = card.getBoundingClientRect().width + gap;
          grid.scrollBy({left: direction * amount, behavior: reducedMotion.matches ? 'auto' : 'smooth'});
        };
        this.querySelector('[data-tile-scroll-left]')?.addEventListener('click', () => scrollByVariant(-1), {signal: this.abort.signal});
        this.querySelector('[data-tile-scroll-right]')?.addEventListener('click', () => scrollByVariant(1), {signal: this.abort.signal});

        this.updateScrollAffordance();
        requestAnimationFrame(() => this.updateScrollAffordance());
      }
      this.addEventListener('click', async event => {
        const link = event.target.closest('[data-tile-variant]');
        if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const picker = this.parentElement.querySelector('variant-picker');
        const form = document.getElementById(this.dataset.formId);
        if (!picker?.selectCombination || !form) return;
        event.preventDefault();
        if (form.dataset.tileLoading === 'true') return;
        const cards = Array.from(this.querySelectorAll('[data-tile-variant]'));
        const selectedIndex = cards.indexOf(link);
        if (selectedIndex >= 0) {
          cards.forEach((card, index) => {
            card.classList.toggle('is-selected', index === selectedIndex);
            if (index === selectedIndex) card.setAttribute('aria-current', 'true');
            else card.removeAttribute('aria-current');
          });
          const selectedValue = this.querySelector('[data-tile-selected-option-value]');
          if (selectedValue && link.dataset.tileOptionValue) selectedValue.textContent = link.dataset.tileOptionValue;
          this.updateProgress(link);
        }
        form.dataset.tileLoading = 'true';
        form.querySelectorAll('[data-tile-checkout]').forEach(checkout => {
          checkout.setAttribute('aria-disabled', 'true');
          checkout.setAttribute('inert', '');
        });
        this.setAttribute('aria-busy', 'true');
        form.querySelectorAll('[type="submit"]').forEach(button => button.disabled = true);
        try {
          await picker.selectCombination({optionValues: JSON.parse(link.dataset.optionValues), productChange: false});
          this.showVariantMedia(form.getAttribute('id'), link.dataset.tileMediaId);
        } catch (error) {
          window.location.assign(link.href);
        } finally {
          delete form.dataset.tileLoading;
          this.removeAttribute('aria-busy');
          form.querySelector('tile-calculator')?.update();
        }
      }, {signal: this.abort.signal});
    }
    updateScrollAffordance() {
      const grid = this.querySelector('.tile-size-grid');
      const shell = this.querySelector('[data-tile-scroll-shell]');
      if (!grid || !shell) return;
      const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth);
      const current = Math.max(0, Math.min(maxScroll, grid.scrollLeft));
      const threshold = 3;
      shell.classList.toggle('has-overflow-left', maxScroll > threshold && current > threshold);
      shell.classList.toggle('has-overflow-right', maxScroll > threshold && current < maxScroll - threshold);
    }
    // The PNG belongs to the card; the large image comes from Shopify's variant media.
    showVariantMedia(formId, mediaId) {
      if (!mediaId) return;
      const gallery = Array.from(document.querySelectorAll('product-gallery'))
        .find(item => item.getAttribute('form') === formId);
      if (!gallery) return;
      const carousel = gallery.querySelector('media-carousel');
      if (!carousel) return;
      const slides = Array.from(carousel.children).filter(item => item.hasAttribute('data-media-id'));
      const index = slides.findIndex(item => item.dataset.mediaId === String(mediaId));
      if (index < 0 || slides[index].hidden) return;
      // Keep the customer's page position stable. Impact already syncs variant media;
      // this force-select only ensures the chosen variant image is active without scrolling the page.
      carousel.select?.(index, { animate: false, force: true });
    }
    updateProgress(selectedCard = null) {
      const progress = this.querySelector('.tile-size-progress');
      const cards = Array.from(this.querySelectorAll('[data-tile-variant]'));
      if (!progress || cards.length === 0) return;

      const selected = selectedCard || cards.find(card => card.classList.contains('is-selected') || card.getAttribute('aria-current') === 'true') || cards[0];
      const selectedIndex = Math.max(0, cards.indexOf(selected));
      const progressRect = progress.getBoundingClientRect();
      if (progressRect.width <= 0) return;

      const centers = cards.map(card => {
        const rect = card.getBoundingClientRect();
        return rect.left + rect.width / 2;
      });

      // The active segment follows the real visual card positions, not equal fractions
      // of the full row. This keeps it centered below the chosen swatch. For the
      // first/last swatch the segment meets the exact start/end of the grey line.
      const startViewport = selectedIndex === 0
        ? progressRect.left
        : (centers[selectedIndex - 1] + centers[selectedIndex]) / 2;
      const endViewport = selectedIndex === cards.length - 1
        ? progressRect.right
        : (centers[selectedIndex] + centers[selectedIndex + 1]) / 2;

      const left = Math.max(0, Math.min(progressRect.width, startViewport - progressRect.left));
      const right = Math.max(left, Math.min(progressRect.width, endViewport - progressRect.left));
      const nextLeft = `${left.toFixed(2)}px`;
      const nextWidth = `${Math.max(1, right - left).toFixed(2)}px`;
      if (progress.style.getPropertyValue('--tile-progress-left') !== nextLeft) {
        progress.style.setProperty('--tile-progress-left', nextLeft);
      }
      if (progress.style.getPropertyValue('--tile-progress-width') !== nextWidth) {
        progress.style.setProperty('--tile-progress-width', nextWidth);
      }
    }
    disconnectedCallback() {
      if (this.progressFrame) cancelAnimationFrame(this.progressFrame);
      this.progressResizeObserver?.disconnect();
      this.abort?.abort();
    }
    text(key, values = {}) {
      let output = this.i18n?.[key] || '';
      for (const [name, value] of Object.entries(values)) output = output.replaceAll(`{${name}}`, String(value));
      return output;
    }
    createMoneyFormatter(currency) {
      try {
        return new Intl.NumberFormat(this.i18n?.locale || 'en-GB', { style: 'currency', currency });
      } catch (_) {
        return new Intl.NumberFormat(this.i18n?.locale || 'en-GB', { style: 'currency', currency: this.dataset.currency || 'DKK' });
      }
    }
  });
}
